import styled from 'styled-components'
import { Button, LoadingSpinner, RowSpacer, ToggleSwitch } from '../atoms'
import { Col, Row } from 'react-grid-system'
import textsData from '@/textData/UserProfileTexts.json'
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks'
import { useMemo, useRef, useState } from 'react'
import {
  useGenerateClientFinderKeyMutation,
} from '@/graphql/generated'
import { ResdysConfigurationForm } from './RedsysConfigurationForm'

const FormWrapper = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 2rem;
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.1);
`

const CardWrapper = styled(FormWrapper)<{ $isDisabled?: boolean }>`
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
  border: ${({ theme, $isDisabled }) => ($isDisabled ? `1px dashed ${theme.colors.background2}` : 'none')};
`

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.colors.dark};
  font-weight: 700;
`

const FieldStyled = styled(Row)`
  padding-bottom: ${({ theme }) => theme.spacing.md};
  p {
    font-weight: 600;
  }
`

const FieldGroup = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: row;
  gap: 0;
`;
const Input = styled.input`
  background: ${({ theme }) => theme.colors.background2};
  border: none;
  border-top-left-radius: ${({ theme }) => theme.border.radius.md};
  border-bottom-left-radius: ${({ theme }) => theme.border.radius.md};
  display: block;
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #212121;
  width: 100%;
  height: auto;
  line-height: unset;
  transition: ease-in-out 0.5s;
  box-sizing: border-box;
`;
const FieldButton = styled.button`
  border: none;
  border-top-right-radius: ${({ theme }) => theme.border.radius.md};
  border-bottom-right-radius: ${({ theme }) => theme.border.radius.md};
  background-color: ${({ theme }) => theme.colors.primary1};
  color: ${({ theme }) => theme.colors.light};
  padding-left: 2rem;
  padding-right: 2rem;
  font-weight: 500;
`

const emptyData = [true, false, false, false, false, false]

export const UserProfileNotificationSettingsForm = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en
  const otherSettings = texts.otherSettings as typeof textsData.en.otherSettings & {
    communicationTitle?: string
    stripeConfig?: {
      title: string
      disabledDescription: string
      comingSoonButton: string
    }
  }
  
  const settingsValuesRef = useRef<boolean[]>(emptyData)

  const [clientFinderKey, setClientFinderKey] = useState<string | null>(null)

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  
  const { open: errorHandle } = useErrorHandlerContext()


  const onChangeHandle = (enabled: boolean, index: number) => {
    settingsValuesRef.current[index] = enabled
  }

  const [generateClientFinderKey, { loading: clientFinderKeyGenerationLoading }] = useGenerateClientFinderKeyMutation()

  const onGenerateClientFinderKeyHandle = () => {
    generateClientFinderKey()
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.otherSettings.clientFinderKeySection.confirmationDialog.title,
          description: texts.otherSettings.clientFinderKeySection.confirmationDialog.description,
        })
        setClientFinderKey(response.data?.generateNewClientFinderSecretKey.key ?? null)
      })
      .catch(errorHandle)
  }

  const onConfirmHandle = () => {
    console.log(settingsValuesRef.current.map((field) => field ? 'true' : 'false'))
    confirmDialogModalOpen({
      title: texts.otherSettings.confirmationDialog.title,
      description: texts.otherSettings.confirmationDialog.description
    })
  }



  const fields = useMemo(() => ([
    {label: texts.otherSettings.comunication.fields[0], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)},
    {label: texts.otherSettings.comunication.fields[1], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)},
    {label: texts.otherSettings.comunication.fields[2], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)},
    {label: texts.otherSettings.comunication.fields[3], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)},
    {label: texts.otherSettings.comunication.fields[4], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)},
    {label: texts.otherSettings.comunication.fields[5], onChange: (enabled: boolean, index: number) => onChangeHandle(enabled, index)}
  ]), [])

  return (
    <>
      <RowSpacer height={3} />
      <SectionTitle>{otherSettings.communicationTitle ?? ''}</SectionTitle>
      <CardWrapper>
        {
          fields.map((field, index) => (
            <FieldStyled justify="between" key={index}>
              <Col xs="content">
                <p>{field.label}</p>
              </Col>
              <Col xs="content">
                <ToggleSwitch
                  onChange={(isEnabled) => onChangeHandle(isEnabled, index)}
                  defaultChecked={settingsValuesRef.current[index]}
                />
              </Col>
            </FieldStyled>
          ))
        }
        <RowSpacer height={2} />
        <FieldStyled justify='between' align='center'>
          <Col md={4}>
            <p>{texts.otherSettings.clientFinderKeySection.label}</p>
          </Col>
          <Col md={8}>
            <FieldGroup>
              <Input
                readOnly
                value={clientFinderKey ?? ''}
                type="text"
              />
              <FieldButton
                onClick={onGenerateClientFinderKeyHandle}
                disabled={clientFinderKeyGenerationLoading}
              >{
                clientFinderKeyGenerationLoading
                  ? <LoadingSpinner />
                  : texts.otherSettings.clientFinderKeySection.button
              }</FieldButton>
            </FieldGroup>

          </Col>
        </FieldStyled>
        <RowSpacer height={3} />
        <Row>
          <Col>
            <Button
              onClick={onConfirmHandle}
            >
              {texts.buttons.confirm}
            </Button>
          </Col>
        </Row>
      </CardWrapper>
      <RowSpacer height={3} />
      <ResdysConfigurationForm />
      <RowSpacer height={3} />
      <SectionTitle>{otherSettings.stripeConfig?.title ?? 'Stripe'}</SectionTitle>
      <CardWrapper $isDisabled>
        <RowSpacer height={1} />
        <Row>
          <Col>
            <p>{otherSettings.stripeConfig?.disabledDescription ?? ''}</p>
          </Col>
        </Row>
        <RowSpacer height={1.5} />
        <Row>
          <Col>
            <Button disabled>{otherSettings.stripeConfig?.comingSoonButton ?? 'Coming soon'}</Button>
          </Col>
        </Row>
      </CardWrapper>
    </>
  )
}